import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';

import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useStates from '@/hooks/pure/useStates';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { productInfoService } from '../api';
import { BaseTable, type RowData, type TableColumnT } from '../components/BaseTable';
import { ProductManagerEllipsisMenu } from '../components/DeleteItem/ProductManagerEllipsisMenu';
import { DetailModal } from '../components/DetailModal/DetailModal';
import { EllipsisCell } from '../components/EllipsisCell/EllipsisCell';
import { ListHeader } from '../components/ListHeader';
import { MergeModal } from '../components/MergeModal';
import useProductStore from '../store/ProductStore';
import type { ProductT } from '../types';

export const LineView = () => {
  const fetchProductList = useProductStore((state) => state.fetchProductList);
  const lineMap = useProductStore((state) => state.lineMap);

  const lineOptions = useMemo(() => {
    return Array.from(lineMap.values());
  }, [lineMap]);

  const [checkedList, setCheckedList] = useState<string[]>([]);
  const mergeModal = useModalState();

  const { state, setValue } = useStates({
    searchValue: '',
  });

  const filteredLineList = useMemo(() => {
    return lineOptions.filter((line) => {
      if (
        state.searchValue &&
        !line.name?.toLowerCase().includes(state.searchValue.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [lineOptions, state.searchValue]);

  return (
    <div className="flex flex-col gap-4 flex-1 overflow-auto">
      <ListHeader
        mergeDisabled={checkedList.length < 2}
        onSearchSubmit={(value) => {
          setValue('searchValue', value);
        }}
        clearSearch={() => {
          setValue('searchValue', '');
        }}
        onMergeClick={() => {
          mergeModal.open();
        }}
      >
        {checkedList.length > 0 && (
          <span className="text-sm text-gray-600">
            {checkedList.length} selected
          </span>
        )}
      </ListHeader>
      <LineTable
        lineList={filteredLineList}
        checkedList={checkedList}
        searchValue={state.searchValue}
        setCheckedList={setCheckedList}
      />
      <Modal status={mergeModal.status} closeModal={mergeModal.close}>
        <MergeModal
          items={checkedList.map((id) => ({
            id,
            name: lineMap.get(id)?.name || '-',
          }))}
          target="line"
          mergeCallback={async (targetId, list) => {
            await productInfoService.mergeLines({
              from: list.filter((id) => id !== targetId),
              to: targetId,
            });
          }}
          onDoneCallback={() => {
            setCheckedList([]);
            fetchProductList(getCurrWorkspaceAlias());
          }}
        />
      </Modal>
    </div>
  );
};

const LineTable = (props: {
  lineList: ProductLineOption[];
  checkedList: string[];
  searchValue: string;
  setCheckedList: Dispatch<SetStateAction<string[]>>;
}) => {
  const detailModalState = useModalState<{
    alias: string | null;
  }>({
    alias: null,
  });
  const lineMap = useProductStore((state) => state.lineMap);
  const productMap = useProductStore((state) => state.groupedProductMap);
  const groupedProductMap = useMemo(() => {
    const map = new Map<string, ProductT[]>();
    lineMap.forEach((line) => {
      const products = Array.from(productMap.values()).filter(
        (product) => product.line_id === line.id,
      );
      map.set(line.id, products);
    });
    return map;
  }, [lineMap, productMap]);
  const { lineList, checkedList, searchValue, setCheckedList } = props;

  const LineTableColumns: TableColumnT<ProductLineOption>[] = [
    {
      columnKey: 'line',
      Header: 'Line',
      Cell: (rowData: RowData<ProductLineOption>) => (
        <span className="truncate">{rowData.rowData.name}</span>
      ),
      className: 'flex-1 min-w-0',
      sortable: true,
      sort: (a, b) => {
        return a.name?.localeCompare(b.name || '') || 0;
      },
    },
    {
      columnKey: 'products',
      Header: 'Products',
      Cell: (rowData: RowData<ProductLineOption>) => {
        const products = groupedProductMap.get(rowData.rowData.id);
        return (
          <EllipsisCell
            dataList={
              products?.map((product) => ({
                id: product.alias,
                name: product.product_name || '',
              })) || []
            }
            columnWidth={400}
          />
        );
      },
      className: 'w-100',
    },
    {
      columnKey: 'action',
      Header: 'Action',
      Cell: (rowData: RowData<ProductLineOption>) => (
        <div className="flex justify-center w-full">
          <ProductManagerEllipsisMenu
            target="line"
            targetId={rowData.rowData.id}
          />
        </div>
      ),
      className: 'w-20',
    },
  ];
  return (
    <>
      <BaseTable
        defaultSortKey="line"
        columns={LineTableColumns}
        data={lineList.map((line) => ({
          rowKey: line.id,
          rowData: line,
        }))}
        searchValue={searchValue}
        onRowClick={(rowKey) =>
          detailModalState.open({ modalState: { alias: rowKey } })
        }
        placeholder={{
          title: 'No line yet',
        }}
        useCheckbox
        checkedList={checkedList}
        setCheckedList={setCheckedList}
      />
      <Modal
        status={detailModalState.status}
        closeModal={detailModalState.close}
      >
        {detailModalState.modalState?.alias && (
          <DetailModal alias={detailModalState.modalState.alias} type="line" />
        )}
      </Modal>
    </>
  );
};
